import { LandingPageConfigQuery } from 'generated/graphql';
import { toRawImgSrc, toRawVideoSrc } from './helpers';
import { defaultReviews, defaultFaqs } from './helpers';
import {
  IBioSection,
  IEmailCaptureSection,
  IFaqsSection,
  IFeaturedBenefit,
  IFeaturesNewSection,
  IIntroductionSection,
  ILandingPagesBuilderSchema,
  ILogosSection,
  IPaidProgramsCarousel,
  IProgramsCarousel,
  IProgramsHighlight,
  ISection,
  ITestimonialsSection,
} from '@playbook-technologies/common-schemas';
import { compact } from 'lodash';
import { DEFAULT_LOGOS } from './Logos';
import { DEFAULT_FEATURES } from './FeaturesNewSection';

export function loadPageConfig(
  influencer: LandingPageConfigQuery['creator'],
  serverConfig: ILandingPagesBuilderSchema | null,
  { primaryColor, colors }: { primaryColor: string; colors: string[] },
): ILandingPagesBuilderSchema {
  if (!influencer) {
    throw new Error('No influencer passed to loadPageConfig()');
  }

  const hasOnlyOTP = influencer?.publishedPrograms.every(
    (p) => p.accessType === 'exclusive',
  );

  const paidProgramsConfig: IPaidProgramsCarousel = {
    enabled: true,
    type: 'PaidProgramsList',
    title: 'One-time Purchases',
    subtitle: '',
    bgColor: colors[2],
    colorIndex: 2,
  };

  const programsConfig: IProgramsCarousel = {
    enabled: true,
    type: 'ProgramsList',
    title: 'Included in subscription',
    subtitle: '',
    bgColor: colors[2],
    colorIndex: 2,
  };

  const sections: ISection[] = compact([
    {
      enabled: true,
      type: 'Introduction',
      title: '',
      subtitle: '',
      bullets: [
        { text: 'Workout logging, scheduling, and more' },
        { text: 'Videos for each exercise' },
        { text: 'One of the best rated apps (⭐️4.9/5)' },
      ],
      coverImage: toRawImgSrc(influencer.mediaCover) as string,
      introVideo: toRawVideoSrc(influencer.mediaWelcome) as string,
      colorIndex: 1,
      bgColor: colors[1],
    } as IIntroductionSection,
    {
      enabled: true,
      type: 'Logos',
      bgColor: colors[2],
      colorIndex: 2,
      logos: DEFAULT_LOGOS,
    } as ILogosSection,
    {
      type: 'EmailCapture',
      enabled: false,
      bgColor: colors[2],
      colorIndex: 2,
      title: '',
      description: '',
      displayAsPopup: true,
    } as IEmailCaptureSection,
    hasOnlyOTP ? paidProgramsConfig : programsConfig,
    {
      enabled: true,
      type: 'Reviews',
      reviews: [...defaultReviews],
      bgColor: colors[1],
      colorIndex: 1,
    },
    hasOnlyOTP ? null : paidProgramsConfig,
    {
      type: 'ProgramsHighlight',
      enabled: false,
      title: 'Programs Highlight',
      bgColor: colors[0],
      colorIndex: 0,
      desc: '',
      programIds: [],
      bullets: [],
    } as IProgramsHighlight,
    {
      type: 'FeaturedBenefit',
      enabled: false,
      title: 'Featured Benefit',
      bgColor: colors[1],
      colorIndex: 1,
      bullets: [],
      coverImage: '',
    } as IFeaturedBenefit,
    {
      enabled: true,
      type: 'Bio',
      title: influencer.bio || 'Online personal trainer',
      desc: influencer.messageProfile,
      coverImage: toRawImgSrc(influencer.mediaAvatar) as string,
      bgColor: colors[0],
      colorIndex: 0,
      introVideo: null,
    } as IBioSection,
    {
      enabled: false,
      type: 'Testimonials',
      title: 'Need to see it to believe it?',
      testimonials: [],
      bgColor: colors[0],
      colorIndex: 0,
    } as ITestimonialsSection,
    {
      enabled: true,
      type: 'FeaturesNew',
      title: 'Program Features',
      bgColor: colors[1],
      colorIndex: 1,
      features: DEFAULT_FEATURES,
    } as IFeaturesNewSection,
    {
      enabled: true,
      type: 'FAQs',
      title: 'Frequently asked questions',
      faqs: defaultFaqs,
      bgColor: colors[2],
      colorIndex: 2,
    } as IFaqsSection,
  ]);

  const data: ILandingPagesBuilderSchema = {
    branding: {
      primaryColor,
      logo: null,
    },
    sections: sections,
    ...serverConfig,
  };

  return data;
}
